import React from 'react';
import { useTranslation } from 'react-i18next';
import cx from 'classnames';
import { CircularProgress } from "@heroui/react";

import { useAppDispatch, useAppSelector } from '@/app/store';
import { numericSort } from '@/app/api/common';

import {
  AppTotal,
  clampStatisticsOffset,
  fetchStatistics,
  formatScreenTimeDuration,
  RoomTotal,
  StatisticsBucket,
  statisticsPeriodValues,
  StatisticsReport,
  StatisticsResult,
  statisticsSortValues,
} from '@/features/appusage/appUsageApi';
import { appUsageScopeKey, appUsageSlice, selectAppUsageScope } from '@/features/appusage/appUsageSlice';

import { HotelInfoPublic, HotelRoom } from '@/features/projects/projectsSlice';

import Button from '@/components/Common/Button';
import FlexRow from '@/components/Common/FlexRow';
import Dropdown, { FieldInfo } from '@/components/Common/Dropdown';
import Grid from '@/components/Common/Grid';

// The shared body of the project- and room-level Statistics tabs: KPI tiles,
// per-app breakdown, buckets-over-time bars, an all-rooms ranking, and an
// embedded screen-time summary — app usage and screen time are visually
// split by a divider (§ user request), never merged into one figure. The
// project tab (ProjectStatistics.tsx) renders this with a room picker and
// the ranking; the room tab (RoomStatistics.tsx) locks `fixedRoomNo` to the
// room it is on, which naturally drops the picker and the ranking (there is
// nothing to rank against) and turns off the screen-time card, since that
// room's own detailed minute timeline already sits above it (RoomScreenTime).
export interface StatisticsPanelProps {
  projectId:			string;
  pub?:					HotelInfoPublic | null;
  fixedRoomNo?:			string;
  showScreentimeCard?:	boolean;	// default true
  subtitle?:			string;		// overrides the default card subtitle
  onAvailability?:		(available: boolean) => void;
  onOpenRoom?:			(roomNo: string) => void;
}

const cardBorder	= "border-solid border-1 border-[#E7E9EF]";
const barTrack		= "#EDEFF3";
const barLaunches	= "#5B7BB4";
const barSeconds	= "#3F9D62";

const Message = (props: {icon: string; title: string; detail?: string; children?: React.ReactNode}) => (
  <FlexRow direction="column"
           align="start"
           className="gap-[8px] py-[24px] max-w-[560px]">
    <FlexRow className="gap-[8px]">
      <span className="material-icons !text-[--Mid-Grey] text-[20px]">
        { props.icon }
      </span>
      <span className="r14 !text-black">
        { props.title }
      </span>
    </FlexRow>
  {props.detail &&
    <span className="text-[--Dark-Grey] text-[12px] leading-[18px] pl-[28px]">
      { props.detail }
    </span>
  }
    { props.children }
  </FlexRow>
);

const Tile = (props: {label: string; children?: React.ReactNode}) => (
  <div className={cx("flex-1 min-w-[170px] rounded-[8px] px-[16px] py-[14px]", cardBorder)}>
    <div className="text-[11px] uppercase tracking-[0.6px] text-[--Dark-Grey] pb-[8px]">
      { props.label }
    </div>
    <div className="text-[20px] font-semibold !text-black leading-[24px]">
      { props.children }
    </div>
  </div>
);

// One row per bucket — a hand-rolled bar, no charting dependency. `metric`
// picks which of launches/seconds this track measures; `max` is the largest
// value across the whole window so bar widths are comparable row to row.
const BucketBar = (props: {
  bucket:		StatisticsBucket;
  label:		string;
  max:			number;
  metric:		"launches" | "seconds";
}) => {
  const value = props.bucket[props.metric];
  const pct = props.max > 0 ? Math.max(2, (value / props.max) * 100) : 0;
  const color = props.metric === "launches" ? barLaunches : barSeconds;

  const text =	props.metric === "launches"
					?	value.toLocaleString()
					:	formatScreenTimeDuration(value);

  return (
  <FlexRow className="gap-[16px] py-[3px]">
    <div className={cx(
          "min-w-[88px] text-right text-[12px]",
          value > 0 ? "!text-black font-medium" : "text-[--Mid-Grey]"
        )}>
      { props.label }
    </div>
    <div className="relative flex-1 h-[16px] rounded-[4px] overflow-hidden"
         style={{ background: barTrack }}>
    {value > 0 &&
      <div className="absolute top-0 bottom-0 left-0 rounded-[4px]"
           style={{ width: pct + '%', background: color }} />
    }
    </div>
    <div className={cx(
          "min-w-[80px] text-[12px]",
          value > 0 ? "!text-black font-medium" : "text-[--Mid-Grey]"
        )}>
      { text }
    </div>
  </FlexRow>
  );
};

function bucketLabel(bucket: StatisticsBucket, granularity: string): string {
  if (!bucket.start)
    return '-';

  const d = new Date(bucket.start * 1000);

  return	granularity === "month"
			?	d.toLocaleDateString(undefined, { month: 'short', year: 'numeric' })
			:	d.toLocaleDateString(undefined, { day: '2-digit', month: '2-digit' });
}

const StatisticsPanel = (props: StatisticsPanelProps) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const isRoomLocked = props.fixedRoomNo !== undefined;

  const [roomNoState, setRoomNoState]	= React.useState(props.fixedRoomNo || "");
  const [period, setPeriod]			= React.useState("day");
  const [offset, setOffset]			= React.useState(0);
  const [sort, setSort]				= React.useState("seconds");
  const [loading, setLoading]			= React.useState(true);
  const [reloads, setReloads]			= React.useState(0);

  const roomNo = isRoomLocked ? (props.fixedRoomNo as string) : roomNoState;

  const projectId = props.projectId;
  const scopeKey = appUsageScopeKey(projectId, roomNo, period, offset, sort);
  const scope = useAppSelector((state) => selectAppUsageScope(state.appusage, scopeKey));

  const onAvailability = props.onAvailability;

  React.useEffect(() => {
    if (!projectId)
      return;

    let cancelled = false;
    const key = appUsageScopeKey(projectId, roomNo, period, offset, sort);

    setLoading(true);

    fetchStatistics({
      projectId:	projectId,
      roomNo:		roomNo,
      period:		period,
      offset:		offset,
      sort:			sort,
    }).then((res: StatisticsResult) => {
      if (cancelled)
        return;

      dispatch(appUsageSlice.actions.setResult({ key: key, result: res }));
      setLoading(false);

      // Only a server-wide 503 or a flat 403 refusal drops the tab. Every
      // other outcome (including "not_found" for a stale room selection)
      // keeps it and explains itself below.
      if (onAvailability)
        onAvailability(res.kind !== "disabled" && res.kind !== "forbidden");
    });

    return () => { cancelled = true; };
  }, [projectId, roomNo, period, offset, sort, reloads, dispatch, onAvailability]);

  const result = scope ? scope.result : null;

  // Only a server-wide 503 or a flat 403 refusal hides the whole tab body —
  // decided after every hook below has run so hook order never depends on
  // this branch (see the early `if (hidden) return null;` at the bottom).
  const hidden =	result != null &&
					(	result.kind === "disabled" ||
						result.kind === "forbidden"
					);

  const report =	result && result.kind === "ok"
					?	result.report
					:	null;

  const roomOptions: FieldInfo[] = React.useMemo(() => {
    const rooms = props.pub?.rooms?.rooms ?? [];
    const opts: FieldInfo[] = [
      { value: "", desc: t('projects:project:statistics:all_rooms') },
      ...[...rooms]
        .sort((a: HotelRoom, b: HotelRoom) => numericSort(a.room_no, b.room_no))
        .map((r: HotelRoom) => ({ value: r.room_no, desc: r.room_no })),
    ];
    return opts;
  }, [props.pub, t]);

  const changeRoom = (val: unknown) => {
    setRoomNoState(typeof val === "string" ? val : "");
    setOffset(0);
  };

  const changePeriod = (p: string) => {
    setPeriod(p);
    setOffset(0);
  };

  const maxOffset = clampStatisticsOffset(period, 1000);

  const openRoom = props.onOpenRoom;

  const kpis = (rep: StatisticsReport) => (
    <FlexRow align="start"
             className="gap-[16px] flex-wrap pb-[8px]">
      <Tile label={t('projects:project:statistics:kpi:launches')}>
        { rep.totals.launches.toLocaleString() }
      </Tile>
      <Tile label={t('projects:project:statistics:kpi:avg_launches_per_day')}>
        { rep.totals.avg_launches_per_day.toFixed(1) }
      </Tile>
      <Tile label={t('projects:project:statistics:kpi:duration')}>
        { formatScreenTimeDuration(rep.totals.seconds) }
      </Tile>
      <Tile label={t('projects:project:statistics:kpi:avg_duration_per_day')}>
        { formatScreenTimeDuration(rep.totals.avg_seconds_per_day) }
      </Tile>
    {rep.all_rooms &&
      <Tile label={t('projects:project:statistics:kpi:rooms_active')}>
        { rep.rooms_active.toLocaleString() }
      </Tile>
    }
    </FlexRow>
  );

  const appColumns = React.useMemo(() => [
    { field: "app",						headerName: t('projects:project:statistics:apps:grid:app'),					flex: 2 },
    { field: "launches",					headerName: t('projects:project:statistics:apps:grid:launches'),				flex: 1 },
    { field: "avg_launches_per_day",		headerName: t('projects:project:statistics:apps:grid:avg_launches_per_day'),	flex: 1,
      valueFormatter: (p: { value?: number | null }) => (p.value ?? 0).toFixed(1) },
    { field: "duration",					headerName: t('projects:project:statistics:apps:grid:duration'),				flex: 1 },
    { field: "avg_duration_per_day",		headerName: t('projects:project:statistics:apps:grid:avg_duration_per_day'),	flex: 1 },
  ], [t]);

  const appRows = React.useMemo(() => {
    if (!report)
      return [];

    return report.apps.map((a: AppTotal) => ({
      app:					a.app,
      launches:				a.launches,
      avg_launches_per_day:	a.avg_launches_per_day,
      duration:				formatScreenTimeDuration(a.seconds),
      avg_duration_per_day:	formatScreenTimeDuration(a.avg_seconds_per_day),
    }));
  }, [report]);

  const roomColumns = React.useMemo(() => [
    { field: "room_no",					headerName: t('projects:project:statistics:rooms:grid:room'),					flex: 1 },
    { field: "launches",					headerName: t('projects:project:statistics:rooms:grid:launches'),				flex: 1 },
    { field: "avg_launches_per_day",		headerName: t('projects:project:statistics:rooms:grid:avg_launches_per_day'),	flex: 1,
      valueFormatter: (p: { value?: number | null }) => (p.value ?? 0).toFixed(1) },
    { field: "duration",					headerName: t('projects:project:statistics:rooms:grid:duration'),				flex: 1 },
    { field: "avg_duration_per_day",		headerName: t('projects:project:statistics:rooms:grid:avg_duration_per_day'),	flex: 1 },
    { field: "screentime",					headerName: t('projects:project:statistics:rooms:grid:screentime'),			flex: 1 },
  ], [t]);

  const roomRows = React.useMemo(() => {
    if (!report)
      return [];

    return report.rooms.map((r: RoomTotal) => ({
      room_no:				r.room_no,
      launches:				r.launches,
      avg_launches_per_day:	r.avg_launches_per_day,
      duration:				formatScreenTimeDuration(r.seconds),
      avg_duration_per_day:	formatScreenTimeDuration(r.avg_seconds_per_day),
      screentime:			r.screentime_seconds === null ? '–' : formatScreenTimeDuration(r.screentime_seconds),
    }));
  }, [report]);

  const bucketsView = (rep: StatisticsReport) => {
    const maxLaunches = Math.max(0, ...rep.buckets.map((b: StatisticsBucket) => b.launches));
    const maxSeconds  = Math.max(0, ...rep.buckets.map((b: StatisticsBucket) => b.seconds));

    return (
    <FlexRow direction="column"
             align="start"
             className="w-full gap-[16px] pt-[8px]">
      <FlexRow direction="column"
               align="start"
               className="w-full gap-[2px]">
        <span className="text-[12px] font-medium text-[--Dark-Grey] pb-[4px]">
          { t('projects:project:statistics:buckets:launches') }
        </span>
      {rep.buckets.map((b: StatisticsBucket, i: number) =>
        <BucketBar key={i}
                   bucket={b}
                   label={bucketLabel(b, rep.granularity)}
                   max={maxLaunches}
                   metric="launches" />
      )}
      </FlexRow>
      <FlexRow direction="column"
               align="start"
               className="w-full gap-[2px]">
        <span className="text-[12px] font-medium text-[--Dark-Grey] pb-[4px]">
          { t('projects:project:statistics:buckets:duration') }
        </span>
      {rep.buckets.map((b: StatisticsBucket, i: number) =>
        <BucketBar key={i}
                   bucket={b}
                   label={bucketLabel(b, rep.granularity)}
                   max={maxSeconds}
                   metric="seconds" />
      )}
      </FlexRow>
    </FlexRow>
    );
  };

  // The ranking only makes sense across more than one room, so it rides the
  // same `all_rooms` flag the KPI tile above uses — it is naturally absent
  // once `fixedRoomNo` pins the query to a single room.
  const roomsRanking = (rep: StatisticsReport) => {
    if (!rep.all_rooms || rep.rooms.length === 0)
      return null;

    return (
    <FlexRow direction="column"
             align="start"
             className="w-full gap-[8px] pt-[16px]">
      <FlexRow className="gap-[16px] flex-wrap">
        <span className="r14 font-semibold !text-black">
          { t('projects:project:statistics:rooms:caption') }
        </span>
        <FlexRow flex={0}
                 justify="end"
                 className="gap-[8px]">
        {statisticsSortValues.map((s: string) =>
          <div key={s}
               className={cx(
                 "cursor-pointer select-none rounded-full px-[16px] py-[6px] text-[13px] border-solid border-1 whitespace-nowrap",
                 sort === s
                 ?	"border-[--Primetime-main] bg-[--primetime-30] text-[--Primetime-main] font-medium"
                 :	"border-[#E7E9EF] text-[--Dark-Grey]"
               )}
               onClick={() => setSort(s)}>
            { t('projects:project:statistics:rooms:sort:' + s) }
          </div>
        )}
        </FlexRow>
      </FlexRow>
      <Grid className="w-full"
            maxHeight="360px"
            showPages={true}
            csvFile={`app-usage-rooms-${projectId}-${period}-${offset}-${sort}.csv`}
            columnDefs={roomColumns}
            rowData={roomRows} />
    {rep.rooms.length < rep.rooms_total &&
      <span className="text-[11px] text-[--Mid-Grey] pt-[2px]">
        { t('projects:project:statistics:rooms:truncated_n', {shown: rep.rooms.length, total: rep.rooms_total}) }
      </span>
    }
    </FlexRow>
    );
  };

  const showScreentimeCard = props.showScreentimeCard !== false;

  const screentimeCard = (rep: StatisticsReport) => {
    if (!showScreentimeCard || !rep.screentime)
      return null;

    return (
    <React.Fragment>
      {/* Visually splits app usage from screen time — two different features
          sharing one filter bar, never one blended figure. */}
      <div className="w-full h-[1px] bg-[#E7E9EF] mt-[8px]" />
      <FlexRow direction="column"
               align="start"
               className={cx("w-full gap-[12px] p-[16px] rounded-[10px]", cardBorder)}>
        <FlexRow className="gap-[16px]">
          <FlexRow direction="column"
                   align="start"
                   flex={0}
                   width="auto">
            <span className="r14 font-semibold !text-black">
              { t('projects:project:statistics:screentime:caption') }
            </span>
            <span className="text-[12px] text-[--Dark-Grey]">
              { t('projects:project:statistics:screentime:hint') }
            </span>
          </FlexRow>
          <FlexRow justify="end"
                    className="gap-[24px]">
            <Tile label={t('projects:project:statistics:kpi:duration')}>
              { formatScreenTimeDuration(rep.screentime.seconds) }
            </Tile>
            <Tile label={t('projects:project:statistics:kpi:avg_duration_per_day')}>
              { formatScreenTimeDuration(rep.screentime.avg_seconds_per_day) }
            </Tile>
          </FlexRow>
        </FlexRow>
      {roomNo && openRoom &&
        <Button caption={t('projects:project:statistics:screentime:open_room')}
                icon="open_in_new"
                layout="white"
                onClick={() => openRoom(roomNo)} />
      }
      </FlexRow>
    </React.Fragment>
    );
  };

  const body = () => {
    if (loading && !report)
      return (
      <FlexRow className="py-[32px]">
        <CircularProgress aria-label="Loading statistics" size="sm" />
      </FlexRow>
      );

    if (!result)
      return null;

    switch (result.kind) {
    case "not_found":
      return <Message icon="search_off"
                      title={t('projects:project:statistics:not_found')} />;

    case "unauthorized":
      return null;

    case "error":
      return (
      <Message icon="cloud_off"
               title={t('projects:project:statistics:load_failed')}
               detail={result.message}>
        <FlexRow flex={0}
                 className="pl-[28px] pt-[8px]">
          <Button caption={t('projects:project:statistics:retry')}
                  icon="refresh"
                  layout="white"
                  onClick={() => setReloads((n: number) => n + 1)} />
        </FlexRow>
      </Message>
      );
    }

    if (!report)
      return null;

    const empty = report.totals.launches === 0 && report.apps.length === 0;

    return (
    <FlexRow direction="column"
             align="start"
             className="w-full gap-[8px]">
      { kpis(report) }

    {empty &&
      <Message icon="hourglass_empty"
               title={t('projects:project:statistics:nothing_recorded')}
               detail={t('projects:project:statistics:nothing_recorded_hint')} />
    }

    {!empty &&
      <FlexRow direction="column"
               align="start"
               className="w-full gap-[8px] pt-[8px]">
        <span className="r14 font-semibold !text-black">
          { t('projects:project:statistics:apps:caption') }
        </span>
        <Grid className="w-full"
              maxHeight="360px"
              showPages={true}
              csvFile={`app-usage-${projectId}-${period}-${offset}.csv`}
              columnDefs={appColumns}
              rowData={appRows} />
      </FlexRow>
    }

    {!empty &&
      <FlexRow direction="column"
               align="start"
               className="w-full pt-[16px]">
        <span className="r14 font-semibold !text-black pb-[4px]">
          { t('projects:project:statistics:buckets:caption') }
        </span>
        { bucketsView(report) }
      </FlexRow>
    }

      { roomsRanking(report) }
      { screentimeCard(report) }
    </FlexRow>
    );
  };

  if (hidden)
    return null;

  return (
  <FlexRow direction="column"
           align="start"
           className="w-full gap-[16px] pt-[16px]">
    <span className="text-[24px] font-bold !text-black">
      { t('projects:project:statistics:title') }
    </span>

    <div className={cx("w-full max-w-[1100px] bg-white rounded-[10px] overflow-hidden", cardBorder)}>
      <FlexRow className={cx("gap-[16px] px-[24px] py-[16px] border-b-1 border-solid border-[#E7E9EF] flex-wrap")}>
        <FlexRow flex={0}
                 justify="center"
                 className="w-[40px] h-[40px] rounded-[8px] bg-[#EEF2FA]">
          <span className="material-icons !text-[#5B7BB4] text-[20px]">
            insights
          </span>
        </FlexRow>
        <FlexRow direction="column"
                 align="start"
                 width="auto">
          <span className="r14 font-semibold !text-black">
            { t('projects:project:statistics:caption') }
          </span>
          <span className="text-[12px] text-[--Dark-Grey]">
            { props.subtitle || t('projects:project:statistics:subtitle') }
          </span>
        </FlexRow>
      {!isRoomLocked &&
        <Dropdown options={roomOptions}
                  value={roomNo}
                  setValue={changeRoom}
                  className="min-w-[220px] w-[220px] flex-none">
          <></>
        </Dropdown>
      }
        <FlexRow flex={0}
                 justify="end"
                 className="gap-[8px]">
        {statisticsPeriodValues.map((p: string) =>
          <div key={p}
               className={cx(
                 "cursor-pointer select-none rounded-full px-[16px] py-[6px] text-[13px] border-solid border-1 whitespace-nowrap",
                 period === p
                 ?	"border-[--Primetime-main] bg-[--primetime-30] text-[--Primetime-main] font-medium"
                 :	"border-[#E7E9EF] text-[--Dark-Grey]"
               )}
               onClick={() => changePeriod(p)}>
            { t('projects:project:screentime:period:' + p) }
          </div>
        )}
        </FlexRow>
        <FlexRow flex={0}
                 justify="end"
                 className="gap-[4px]">
          <span className="material-icons cursor-pointer !text-[--Dark-Grey] text-[20px]"
                title={t('projects:project:statistics:prev') as string}
                onClick={() => setOffset((n: number) => clampStatisticsOffset(period, n + 1))}>
            chevron_left
          </span>
          <span className="text-[13px] text-[--Dark-Grey] min-w-[24px] text-center">
            { offset }
          </span>
          <span className={cx(
                  "material-icons text-[20px]",
                  offset > 0 ? "cursor-pointer !text-[--Dark-Grey]" : "text-[--Lines] pointer-events-none"
                )}
                title={t('projects:project:statistics:next') as string}
                onClick={() => setOffset((n: number) => Math.max(0, n - 1))}>
            chevron_right
          </span>
        </FlexRow>
        <FlexRow flex={0}
                 justify="end"
                 width="auto">
          <span className="text-[11px] text-[--Mid-Grey]">
            { offset >= maxOffset ? t('projects:project:statistics:oldest') : '' }
          </span>
        </FlexRow>
      </FlexRow>

      <FlexRow direction="column"
               align="start"
               className="px-[24px] py-[20px]">
        { body() }
      </FlexRow>
    </div>
  </FlexRow>
  );
};

export default StatisticsPanel;
